import { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// eslint-disable-next-line no-restricted-imports
import { MantineProvider, useMantineTheme } from '@mantine/core';
import TopBar from './widgets/TopBar/TopBar';
import ErrorFallback from './widgets/ErrorFallback';
import EmptyFallback from './widgets/EmptyFallback';
import MoreButton from './widgets/MoreButton';
import TableMenu from './widgets/TableMenu';
import { useDispatch, useSelector } from 'react-redux';
import './style.css';
import {
  updateColumnOrderByTableName,
  updateColumnSizingByTableName,
  updateColumnVisibilityByTableName,
  updateDensity,
  updateShowColumnFilters,
  updateColumnPinningByTableName,
  updateIsFullScreen,
  updateCanShowShimmer,
  resetViewedRows,
  TABLE_NAMES,
  sendNewTableSettings,
} from '../../../redux_store';
import { compare } from 'fast-json-patch';
import {
  TABLE_DEFAULT_SETTINGS,
  tableBodyHoverProp,
  tableBodyProp,
  tableHeadCellProps,
} from './table_state_styles';
import { useForceRepaintOnVisibility } from './helpers';
import { useHeaderLongPressReorder } from './useHeaderLongPressReorder';
import { useMediaQuery } from '@material-ui/core';
import theme from '../../../config/theme';

let timeoutIds = {};
let _delayed = false;
let _delayedTimeoutId = null;

const Table = memo(props => {
  const {
    columns,
    data,
    fetchNextPage,
    isError,
    fetchError,
    isFetching,
    isLoading,
    totalRowCount,
    renderRowActionMenuItems,
    sorting,
    setSorting,
    filter,
    setFilters,
    tableKey,
    enableRowSelection,
    onRowClick,
    maxHeight,
    renderRowActions,
    rowActionsTitleSize,
    rowSelection,
    setRowSelection,
    enableEditing,
    showRefreshTableButton,
    reloadData,
    state = {},
    menuActions,
    tableMenuPopoverOptions,
    tableMenuStatus,
    tableTitle,
    customFilterWidget,
    removeVerticalCellPadding = false,
  } = props;

  // ------------ Fix for bug with white screen
  useForceRepaintOnVisibility('.mantine-Table-root');

  const dispatch = useDispatch();
  const tableContainerRef = useRef(null);
  const totalFetched = data.length;
  const tableName = TABLE_NAMES[tableKey];
  const totalDBRowCount = totalRowCount ?? 50;
  const {
    isFullScreen,
    settings,
    settings: {
      commonTable: { density, showColumnFilters },
    },
    canShowShimmer,
  } = useSelector(state => state.table);
  const rowVirtualizerInstanceRef = useRef(null);

  // -------------------------- Table settings from API --------------------------
  const { columnOrder, columnVisibility, columnSizes, columnPinning } = settings[tableKey] ?? {};
  const [_cashedSortAndFilter, _setCashedSortAndFilter] = useState('');
  const [_showColumnFilters, _setShowColumnFilters] = useState(showColumnFilters);
  const [_columnSizing, _setColumnSizing] = useState({ ...(columnSizes || {}) });
  const [_columnOrder, _setColumnOrder] = useState([...(columnOrder || [])]);
  const [_columnVisibility, _setColumnVisibility] = useState({ ...(columnVisibility || {}) });
  const [_density, _setDensity] = useState(density || 'md');
  const [_columnPinning, _setColumnPinning] = useState({
    ...(columnPinning
      ? {
          right: columnPinning.right || [],
          left: columnPinning.left || [],
        }
      : {
          right: [],
          left: [],
        }),
  });
  const [_isFullScreen, _setIsFullScreen] = useState(isFullScreen);
  // ------------------------------------------------------------------------------

  const [_prevSettings, _setPrevSettings] = useState(settings);
  const { isInfoPaneExpanded, currentPage } = useSelector(state => state.infoPane);
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const [delayed, setDelayed] = useState(true);

  const setCanShowShimmer = newValue => {
    dispatch(updateCanShowShimmer(newValue));
  };
  const updateDelayed = async () => {
    await clearTimeout(_delayedTimeoutId);
    _delayedTimeoutId = setTimeout(() => {
      if (_delayed) {
        setDelayed(true);
      }
    }, 300);
  };
  const removeDelayed = async () => {
    await clearTimeout(_delayedTimeoutId);
    setDelayed(false);
  };

  useEffect(() => {
    _delayed = isLoading || isFetching;
    if (_delayed) {
      updateDelayed();
    } else {
      removeDelayed();
      setCanShowShimmer(false);
    }
  }, [isLoading, isFetching]);

  const debounce = (callback, wait, timeoutIdKey) => {
    return async (...args) => {
      await clearTimeout(timeoutIds[timeoutIdKey]);
      timeoutIds[timeoutIdKey] = await setTimeout(() => {
        callback(...args);
      }, wait);
    };
  };

  const updateCommonTableWithDelay = debounce(
    () => {
      const patches = compare(settings.commonTable, {
        ...settings.commonTable,
        density: _density,
        showColumnFilters: _showColumnFilters,
      });
      dispatch(
        sendNewTableSettings({
          path: 'commonTable',
          patch: patches,
        })
      );

      dispatch(updateDensity(_density));
      dispatch(updateShowColumnFilters(_showColumnFilters));
    },
    2000,
    'commonTable'
  );

  const updateTableSettingsByNameWithDelay = debounce(
    () => {
      const patches = compare(settings[tableKey], {
        ...settings[tableKey],
        columnPinning: _columnPinning,
        columnSizes: _columnSizing,
        columnVisibility: _columnVisibility,
        columnOrder: _columnOrder,
      });
      dispatch(
        sendNewTableSettings({
          path: tableKey,
          patch: patches,
        })
      );

      // update redux store
      dispatch(
        updateColumnSizingByTableName({
          newColumnSizing: _columnSizing,
          tableKey: tableKey,
        })
      );
      dispatch(
        updateColumnPinningByTableName({
          newColumnPinning: _columnPinning,
          tableKey: tableKey,
        })
      );
      dispatch(
        updateColumnOrderByTableName({
          newOrder: _columnOrder,
          tableKey: tableKey,
        })
      );
      dispatch(
        updateColumnVisibilityByTableName({
          newVisibility: _columnVisibility,
          tableKey: tableKey,
        })
      );
    },
    2000,
    tableKey
  );

  const fetchMoreOnBottomReached = useCallback(
    containerRefElement => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (
          scrollHeight - scrollTop - clientHeight < 1800 &&
          !isFetching &&
          totalFetched < totalDBRowCount
        ) {
          fetchNextPage((data || []).length);
        }
      }
    },
    [fetchNextPage, isFetching, totalFetched, totalDBRowCount]
  );

  const onIsFullScreenChange = useCallback(
    e => {
      dispatch(
        updateIsFullScreen({
          newIsFullScreen: e,
        })
      );
      _setIsFullScreen(e);
    },
    [_isFullScreen]
  );

  useEffect(() => {
    const newCashedSortAndFilter = JSON.stringify([...(sorting || []), ...(filter || [])]);
    if (_cashedSortAndFilter !== newCashedSortAndFilter) {
      if (rowVirtualizerInstanceRef.current) {
        //scroll to the top of the table when sorting changes
        try {
          rowVirtualizerInstanceRef.current.scrollToIndex(0);
        } catch (error) {
          console.error(error);
        }
      }
      setCanShowShimmer(true);
      _setCashedSortAndFilter(newCashedSortAndFilter);
    }
  }, [sorting, filter]);

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  const tableMenu = useMemo(
    () =>
      tableMenuPopoverOptions ? (
        <TableMenu
          tableMenuPopoverOptions={tableMenuPopoverOptions}
          tableMenuStatus={tableMenuStatus}
          isLoading={delayed || canShowShimmer}
        />
      ) : (
        ''
      ),
    [tableMenuPopoverOptions, delayed, tableMenuStatus]
  );

  useEffect(() => {
    if (JSON.stringify(settings) !== JSON.stringify(_prevSettings)) {
      _setPrevSettings(settings);
    }
  }, [settings]);

  useEffect(() => {
    updateTableSettingsByNameWithDelay();
  }, [_columnPinning, _columnSizing, _columnVisibility, _columnOrder]);

  useEffect(() => {
    updateCommonTableWithDelay();
    Array.from(document.getElementsByClassName('density-sensible')).forEach(e => {
      e.style.gap = _density === 'xl' ? '16px' : _density === 'md' ? '8px' : '5px';
    });
  }, [_density, _showColumnFilters]);

  const mantineTableBodyProps = useMemo(
    () => ({
      sx: {
        ...(onRowClick ? tableBodyHoverProp : tableBodyProp),
        '& td:last-child': {
          '& .mantine-Skeleton-root': {
            marginLeft: !!renderRowActionMenuItems || !!renderRowActions ? 12 : 0,
          },
        },
        '& td:has(.more-button)': {
          padding: 0,
        },
        ...(removeVerticalCellPadding && {
          '& td': {
            paddingTop: '2px !important',
            paddingBottom: '2px !important',
            border: 'none !important',
          },
        }),
        '& tr:has(.table-fallback):hover td': {
          backgroundColor: 'transparent !important',
          cursor: 'default',
        },
        '& tr:has(.table-fallback) td': {
          backgroundColor: 'transparent !important',
          border: 'none !important',
        },
      },
    }),
    [delayed, _columnPinning, removeVerticalCellPadding]
  );

  const _renderRowActions = useMemo(
    () =>
      renderRowActions
        ? renderRowActions
        : ({ row, table }) => (
            <MoreButton
              row={row}
              canShowSkeletons={table?.options?.state?.canShowSkeletons}
              actions={renderRowActionMenuItems}
            />
          ),
    [renderRowActions, renderRowActionMenuItems, delayed]
  );

  const renderTopToolbar = useCallback(
    ({ table }) => (
      <TopBar
        reloadData={reloadData}
        rowVirtualizerInstanceRef={rowVirtualizerInstanceRef}
        setCanShowShimmer={setCanShowShimmer}
        showRefreshTableButton={showRefreshTableButton}
        showFiltersButton={!!filter}
        checked={_showColumnFilters}
        setFilters={setFilters}
        setChecked={_setShowColumnFilters}
        table={table}
        menuActions={menuActions}
        tableTitle={tableTitle}
        sorting={sorting}
        setSorting={setSorting}
        columns={columns}
        customFilterWidget={customFilterWidget}
      />
    ),
    [filter, _showColumnFilters, menuActions, totalFetched, sorting, customFilterWidget, tableTitle]
  );

  const mantineToolbarAlertBannerProps = useMemo(
    () =>
      isError && !fetchError
        ? {
            color: 'red',
            children: 'Error loading data',
          }
        : undefined,
    [isError, fetchError]
  );

  const _data = useMemo(
    () => [
      ...data,
      ...(!fetchError && (totalDBRowCount !== totalFetched || (totalFetched === 0 && isFetching))
        ? Array.from(
            { length: totalFetched === 0 ? 25 : Math.min(totalDBRowCount - totalFetched, 30) },
            () => ({
              showShimmer: true,
            })
          )
        : []),
    ],
    [data, totalDBRowCount, totalFetched, fetchError, isFetching]
  );

  const _columns = useMemo(() => columns, [columns]);

  useEffect(() => {
    return () => {
      dispatch(resetViewedRows());
    };
  }, []);

  const mantinePaperProps = useMemo(
    () => ({
      style: {
        maxWidth: `calc(100vw - ${
          _isFullScreen && currentPage?.uuid ? (isInfoPaneExpanded && isDesktop ? 360 : 45) : 0
        }px)`,
        transition: '0.3s',
        border: 'none',
        boxShadow: 'none',
      },
      sx: {
        minHeight: '100%',
        height: '100%',
        maxHeight: maxHeight + 100,
      },
    }),
    [_isFullScreen, isInfoPaneExpanded, isDesktop, maxHeight]
  );

  const mantineTableContainerProps = useMemo(
    () => ({
      sx: {
        minHeight: '100px',
        height:
          maxHeight && !_isFullScreen
            ? maxHeight + 60
            : _isFullScreen
            ? 'calc(100% - 70px)'
            : 'calc(100% - 60px)',

        '&::-webkit-scrollbar-thumb': {
          background: 'grey',
          borderRadius: 10,
        },
        '&::-webkit-scrollbar': {
          width: 0,
          maxHeight: 10,
          background: 'white',
        },
      },
      style: {
        overflowY: 'auto',
        overflowX: 'auto',
        scrollbarWidth: 'auto',
        msOverflowStyle: 'auto',
      },
      onScroll: event => fetchMoreOnBottomReached(event.target),
    }),
    [columns, data, fetchNextPage, isFetching, totalFetched, totalDBRowCount]
  );

  const renderEmptyRowsFallback = useCallback(() => {
    if (isFetching || isLoading) return <></>;

    if (fetchError) {
      const hasActiveFilters = !!filter?.some(
        ({ value }) => value.toString().replaceAll(' ', '').length > 0
      );

      return (
        <ErrorFallback
          error={fetchError}
          onRetry={reloadData}
          hasActiveFilters={hasActiveFilters}
        />
      );
    }

    return <EmptyFallback />;
  }, [isFetching, isLoading, fetchError, reloadData, filter, sorting]);

  const _initSettings = useMemo(
    () => ({
      manualFiltering: !enableEditing,
      manualSorting: !enableEditing,
      enableFilters: !enableEditing,
      initialState: {
        pagination: { pageSize: 15 },
        density: 'md',
        showColumnFilters: true,
        columnPinning: {
          right: !!renderRowActionMenuItems || !!renderRowActions ? ['mrt-row-actions'] : [],
          left: !!enableRowSelection ? ['mrt-row-select'] : [],
        },
      },
      onColumnOrderChange: _setColumnOrder,
      enableRowVirtualization: false,
      enableColumnVirtualization: false,
      onColumnVisibilityChange: _setColumnVisibility,
      onColumnSizingChange: _setColumnSizing,
      onDensityChange: _setDensity,
      onColumnPinningChange: _setColumnPinning,
      onIsFullScreenChange: onIsFullScreenChange,
      enableRowSelection: enableRowSelection,
      enableEditing: enableEditing,
      enableRowActions: !!renderRowActionMenuItems || !!renderRowActions,
      mantineTableBodyRowProps: enableRowSelection
        ? null
        : prop => ({
            onClick: onRowClick ? () => onRowClick(prop.row.original) : null,
            sx: onRowClick ? { cursor: 'pointer' } : {},
          }),
    }),
    []
  );

  const tableRef = useRef(null);
  const baseHeadCellProps = removeVerticalCellPadding
    ? {
        ...tableHeadCellProps,
        sx: {
          ...tableHeadCellProps?.sx,
          '& .mantine-TableHeadCell-Content': {
            paddingTop: '4px !important',
            paddingBottom: '4px !important',
            border: 'none !important',
          },
        },
      }
    : tableHeadCellProps;
  const headCellProps = useHeaderLongPressReorder(
    {
      getColumn: id => tableRef.current.getColumn(id),
      getState: () => tableRef.current.getState(),
      setDraggingColumn: c => tableRef.current.setDraggingColumn(c),
      setHoveredColumn: c => tableRef.current.setHoveredColumn(c),
      setColumnOrder: o => tableRef.current.setColumnOrder(o),
      getHeadCellElements: () => tableRef.current.refs.tableHeadCellRefs.current,
    },
    baseHeadCellProps
  );

  const table = useMantineReactTable({
    ...TABLE_DEFAULT_SETTINGS,
    ..._initSettings,
    columns: _columns,
    data: _data,
    enableDensityToggle: !removeVerticalCellPadding,
    mantineTableContainerProps: {
      ref: tableContainerRef,
      ...mantineTableContainerProps,
    },
    mantineTableHeadCellProps: headCellProps,
    displayColumnDefOptions:
      !!renderRowActionMenuItems || !!renderRowActions
        ? {
            'mrt-row-actions': {
              header: tableMenu,
              size: rowActionsTitleSize ?? 50,
            },
          }
        : {},
    mantinePaperProps: mantinePaperProps,
    mantineSkeletonProps: { animate: false },
    mantineToolbarAlertBannerProps: mantineToolbarAlertBannerProps,
    state: {
      isLoading: false,
      sorting: sorting || [],
      filter: filter || [],
      showAlertBanner: isError && !fetchError,
      showProgressBars: false,
      showLoadingOverlay: false,
      showSkeletons: false,
      canShowSkeletons: canShowShimmer,
      showGlobalFilter: false,
      tableName: tableName,
      rowSelection: rowSelection ?? {},
      showColumnFilters: _showColumnFilters,
      columnOrder: _columnOrder,
      columnVisibility: _columnVisibility,
      columnSizing: _columnSizing,
      density: _density,
      columnPinning: _columnPinning,
      isFullScreen: _isFullScreen,
      ...state,
    },
    renderRowActions: _renderRowActions,
    renderTopToolbar: renderTopToolbar,
    mantineTableBodyProps: mantineTableBodyProps,
    renderSkeleton: () => null,
    onColumnFiltersChange: setFilters,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    renderEmptyRowsFallback: renderEmptyRowsFallback,
    mantineTableProps: {
      highlightOnHover: !fetchError && totalFetched > 0,
    },

    enableStickyHeader: true,
    rowVirtualizerInstanceRef: rowVirtualizerInstanceRef,
    enableRowVirtualization: true,
    rowVirtualizerProps: {
      overscan: 6,
      // estimateSize: index => () => 70,
    },
    enableSortingRemoval: false,
    icons: {
      IconArrowsSort: () => <div className={'sort-icon'} />,
    },
  });
  tableRef.current = table;

  return <MantineReactTable table={table} renderSkeleton={() => null} />;
});

const queryClient = new QueryClient();

const QhsTable = memo(props => {
  return (
    <TableWrapper columns={props?.columns}>
      <Table {...props} />
    </TableWrapper>
  );
});

export default QhsTable;

const TableWrapper = memo(({ children, columns }) => {
  const { settings } = useSelector(state => state.table);
  const globalTheme = useMantineTheme();

  if (!columns || !columns.length || !settings) {
    return <div />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <MantineProvider
        theme={{
          ...globalTheme,
          primaryColor: 'blue',
          primaryShade: 5,
        }}
      >
        {children}
      </MantineProvider>
    </QueryClientProvider>
  );
});
